import React, { memo, useMemo } from 'react';

import {
  arrayOf, bool, func, node, oneOfType, shape, string,
} from 'prop-types';
import { Product, ProductVariant } from '@picsio/icons';

import { getProductVariantKey } from '../../../../store/helpers/assets/aggregateProductVariants';
import Tag from '../../../Tag';
import CollapsibleGroup from '../CollapsibleGroup';
import { getListingIconColor, MASTER_ICON_COLOR } from './helpers/iconColors';
import s from './styles.module.scss';

const ProductLinkChip = ({
  chipKey,
  title,
  isVariant,
  iconColor,
  disabled,
  readonly,
  hideCloseBtn,
  closeDisabled,
  tooltipTextClose,
  onNavigate,
  onDetach,
}) => {
  const isFieldDisabled = Boolean(disabled);
  const shouldHideClose = isFieldDisabled || hideCloseBtn;
  const isCloseDisabled = !shouldHideClose && closeDisabled;

  const icon = useMemo(() => (
    // eslint-disable-next-line jsx-control-statements/jsx-use-if-tag
    isVariant ? <ProductVariant /> : <Product />
  ), [isVariant]);

  return (
    <Tag
      key={chipKey}
      type="product"
      className={s.chip}
      text={title}
      icon={icon}
      iconColor={iconColor}
      showNumber={false}
      tooltipText={title}
      tooltipTextClose={tooltipTextClose}
      hideCloseBtn={shouldHideClose}
      closeDisabled={isCloseDisabled}
      onClick={(readonly || isFieldDisabled) ? undefined : onNavigate}
      onClose={shouldHideClose ? null : onDetach}
    />
  );
};

ProductLinkChip.propTypes = {
  chipKey: string.isRequired,
  title: string.isRequired,
  isVariant: bool.isRequired,
  iconColor: string.isRequired,
  disabled: bool,
  readonly: bool,
  hideCloseBtn: bool,
  closeDisabled: bool,
  tooltipTextClose: oneOfType([string, func, node]).isRequired,
  onNavigate: func.isRequired,
  onDetach: func.isRequired,
};

ProductLinkChip.defaultProps = {
  disabled: false,
  readonly: false,
  hideCloseBtn: false,
  closeDisabled: false,
};

const getChipCloseState = ({
  isLiveLayer,
  actionable,
  detachable,
  tooltipTextClose,
  tooltipTextCloseReadOnly,
}) => {
  // Live/Published: always show disabled X + draft tooltip (even when not navigable).
  if (isLiveLayer) {
    return {
      hideCloseBtn: false,
      closeDisabled: true,
      tooltipTextClose: tooltipTextCloseReadOnly,
    };
  }

  const closeDisabled = actionable && !detachable;
  return {
    hideCloseBtn: !actionable,
    closeDisabled,
    tooltipTextClose: closeDisabled ? tooltipTextCloseReadOnly : tooltipTextClose,
  };
};

const ProductLinksSection = memo(({
  title,
  titleIcon,
  products,
  variants,
  isMaster,
  disabled,
  tooltipTextClose,
  tooltipTextCloseReadOnly,
  isProductActionable,
  isProductDetachable,
  isVariantActionable,
  isVariantDetachable,
  onNavigateProduct,
  onNavigateVariant,
  onDetachProduct,
  onDetachVariant,
}) => {
  const hasContent = products.length > 0 || variants.length > 0;
  if (!hasContent) return null;

  return (
    <CollapsibleGroup
      title={title}
      titleIcon={titleIcon}
      contentClassName={s.chips}
      dataQa={`product-links-${isMaster ? 'master' : 'channel'}`}
    >
      {products.map((product) => {
        const chipKey = isMaster
          ? `product:${product._id}`
          : `listing:${product._id}:${product.isDraft === true ? 'draft' : 'live'}`;
        const productTitle = product.title || product._id;
        const actionable = isProductActionable(product);
        const detachable = isProductDetachable(product);
        const isLiveLayer = !isMaster && product.isDraft !== true;
        const closeState = getChipCloseState({
          isLiveLayer,
          actionable,
          detachable,
          tooltipTextClose,
          tooltipTextCloseReadOnly,
        });

        return (
          <ProductLinkChip
            key={chipKey}
            chipKey={chipKey}
            title={productTitle}
            isVariant={false}
            iconColor={isMaster ? MASTER_ICON_COLOR : getListingIconColor(product.isDraft === true)}
            disabled={disabled}
            readonly={!actionable}
            hideCloseBtn={closeState.hideCloseBtn}
            closeDisabled={closeState.closeDisabled}
            tooltipTextClose={closeState.tooltipTextClose}
            onNavigate={() => onNavigateProduct(product)}
            onDetach={() => onDetachProduct(product, { isMaster })}
          />
        );
      })}
      {variants.map((variant) => {
        const baseKey = getProductVariantKey(variant);
        const chipKey = variant.type === 'listingVariant'
          ? `${baseKey}:${variant.isDraft === true ? 'draft' : 'live'}`
          : baseKey;
        const variantTitle = variant.title || variant.variantId;
        const actionable = isVariantActionable(variant);
        const detachable = isVariantDetachable(variant);
        const isLiveLayer = !isMaster
            && variant.type === 'listingVariant'
            && variant.isDraft !== true;
        const closeState = getChipCloseState({
          isLiveLayer,
          actionable,
          detachable,
          tooltipTextClose,
          tooltipTextCloseReadOnly,
        });

        return (
          <ProductLinkChip
            key={chipKey}
            chipKey={chipKey}
            title={variantTitle}
            isVariant
            iconColor={isMaster ? MASTER_ICON_COLOR : getListingIconColor(variant.isDraft === true)}
            disabled={disabled}
            readonly={!actionable}
            hideCloseBtn={closeState.hideCloseBtn}
            closeDisabled={closeState.closeDisabled}
            tooltipTextClose={closeState.tooltipTextClose}
            onNavigate={() => onNavigateVariant(variant)}
            onDetach={() => onDetachVariant(variant)}
          />
        );
      })}
    </CollapsibleGroup>
  );
});

ProductLinksSection.propTypes = {
  title: string.isRequired,
  titleIcon: node,
  products: arrayOf(shape({})).isRequired,
  variants: arrayOf(shape({})).isRequired,
  isMaster: bool,
  disabled: bool,
  tooltipTextClose: string.isRequired,
  tooltipTextCloseReadOnly: oneOfType([string, func, node]).isRequired,
  isProductActionable: func.isRequired,
  isProductDetachable: func.isRequired,
  isVariantActionable: func.isRequired,
  isVariantDetachable: func.isRequired,
  onNavigateProduct: func.isRequired,
  onNavigateVariant: func.isRequired,
  onDetachProduct: func.isRequired,
  onDetachVariant: func.isRequired,
};

ProductLinksSection.defaultProps = {
  titleIcon: null,
  isMaster: false,
  disabled: false,
};

export default ProductLinksSection;
